import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';

const KEY_MAP = {
  ArrowUp: [0, -1], KeyW: [0, -1], w: [0, -1], W: [0, -1], Up: [0, -1],
  ArrowDown: [0, 1], KeyS: [0, 1], s: [0, 1], S: [0, 1], Down: [0, 1],
  ArrowLeft: [-1, 0], KeyA: [-1, 0], a: [-1, 0], A: [-1, 0], Left: [-1, 0],
  ArrowRight: [1, 0], KeyD: [1, 0], d: [1, 0], D: [1, 0], Right: [1, 0],
};

const SWIPE_MIN = 24;      // px before a drag counts as a swipe
const TAP_MAX = 14;        // px of travel still counted as a tap

/**
 * Merges keyboard, swipe and on-screen D-pad into a single discrete
 * `input:move` stream of {dx, dz} grid steps. Game logic never asks where a
 * hop came from.
 */
export class InputSystem {
  constructor(domElement) {
    this.dom = domElement;
    this.touchStart = null;
    this.swiped = false;
    this.bound = [];

    this.onKeyDown = (e) => {
      if (e.repeat) return;
      // Typing in the config panel must not hop the player or start game.
      if (e.target?.closest?.('input, textarea, select')) return;
      if (e.code === 'Backquote') return;

      const dir = KEY_MAP[e.code] || KEY_MAP[e.key];

      if (gameState.game.isDemo || !gameState.game.started || gameState.game.over) {
        if (e.code !== 'KeyM' && e.key !== 'm' && e.key !== 'M') {
          e.preventDefault();
          eventBus.emit(Events.INPUT_START);
          if (dir) {
            setTimeout(() => {
              eventBus.emit(Events.INPUT_MOVE, { dx: dir[0], dz: dir[1] });
            }, 10);
          }
          return;
        }
      }

      if (dir) {
        e.preventDefault();
        eventBus.emit(Events.INPUT_MOVE, { dx: dir[0], dz: dir[1] });
        return;
      }

      const keyLower = (e.key || '').toLowerCase();
      if (e.code === 'KeyM' || keyLower === 'm') eventBus.emit(Events.INPUT_MUTE);
      else if (e.code === 'KeyP' || keyLower === 'p') eventBus.emit(Events.INPUT_PAUSE);
      else if (e.code === 'Enter' || e.code === 'Space' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        eventBus.emit(Events.INPUT_START);
      }
    };

    this.onTouchStart = (e) => {
      const t = e.changedTouches[0];
      this.touchStart = { x: t.clientX, y: t.clientY };
      this.swiped = false;
    };

    this.onTouchMove = (e) => {
      if (!this.touchStart || this.swiped) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - this.touchStart.x;
      const dy = t.clientY - this.touchStart.y;
      if (Math.hypot(dx, dy) < SWIPE_MIN) return;
      e.preventDefault();
      this.swiped = true;
      if (Math.abs(dx) > Math.abs(dy)) {
        eventBus.emit(Events.INPUT_MOVE, { dx: Math.sign(dx), dz: 0 });
      } else {
        eventBus.emit(Events.INPUT_MOVE, { dx: 0, dz: Math.sign(dy) });
      }
    };

    this.onTouchEnd = (e) => {
      if (!this.touchStart) return;
      const t = e.changedTouches[0];
      const travel = Math.hypot(t.clientX - this.touchStart.x, t.clientY - this.touchStart.y);
      // A plain tap on the field is the most common move: hop forward.
      if (!this.swiped && travel < TAP_MAX) {
        eventBus.emit(Events.INPUT_MOVE, { dx: 0, dz: -1 });
      }
      this.touchStart = null;
    };

    this.listen(window, 'keydown', this.onKeyDown);
    this.listen(this.dom, 'touchstart', this.onTouchStart, { passive: true });
    this.listen(this.dom, 'touchmove', this.onTouchMove, { passive: false });
    this.listen(this.dom, 'touchend', this.onTouchEnd, { passive: true });

    this.bindPad();
  }

  /** On-screen D-pad — the reliable fallback when swipes get fiddly. */
  bindPad() {
    const pad = document.getElementById('dpad');
    if (!pad) return;
    const press = (e) => {
      const btn = e.target.closest('[data-dir]');
      if (!btn) return;
      e.preventDefault();
      const [dx, dz] = btn.dataset.dir.split(',').map(Number);
      eventBus.emit(Events.INPUT_MOVE, { dx, dz });
      btn.classList.add('active');
      setTimeout(() => btn.classList.remove('active'), 90);
    };
    this.listen(pad, 'touchstart', press, { passive: false });
    this.listen(pad, 'mousedown', press);
  }

  listen(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.bound.push([target, type, fn, opts]);
  }

  dispose() {
    for (const [t, type, fn, opts] of this.bound) t.removeEventListener(type, fn, opts);
    this.bound.length = 0;
  }
}
